import '../assets/NcInputField-CZnSCalG.css';
import { N as NcButton } from "./NcButton-Bhuckhtj.mjs";
import { G as GenRandomId } from "./GenRandomId-CMooMQt0.mjs";
import { A as AlertCircle } from "./AlertCircleOutline-DWBCJkLM.mjs";
import { C as Check } from "./Check-_j2mQvkT.mjs";
import { resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, mergeProps, toDisplayString, createCommentVNode, withDirectives, renderSlot, vShow, createBlock, withCtx, createTextVNode } from "vue";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
const _sfc_main = {
  name: "NcInputField",
  components: {
    NcButton,
    AlertCircle,
    Check
  },
  inheritAttrs: false,
  props: {
    /**
     * The value of the input field
     * If type is 'number' and a number is passed as value than the type of `update:value` will also be 'number'
     */
    modelValue: {
      type: [String, Number],
      required: true
    },
    /**
     * The type of the input element
     */
    type: {
      type: String,
      default: "text",
      validator: (value) => [
        "text",
        "password",
        "email",
        "tel",
        "url",
        "search",
        "number"
      ].includes(value)
    },
    /**
     * The input label, always provide one for accessibility purposes.
     * This will also be used as a placeholder unless the placeholder
     * prop is populated with a different string.
     *
     * Note: If the background color is not `--color-main-background` consider using an external label instead (see `labelOutside`).
     */
    label: {
      type: String,
      default: void 0
    },
    /**
     * Pass in true if you want to use an external label. This is useful
     * if you need a label that looks different from the one provided by
     * this component
     */
    labelOutside: {
      type: Boolean,
      default: false
    },
    /**
     * The placeholder of the input. This defaults as the string that's
     * passed into the label prop. In order to remove the placeholder,
     * pass in an empty string.
     */
    placeholder: {
      type: String,
      default: void 0
    },
    /**
     * Controls whether to display the trailing button.
     */
    showTrailingButton: {
      type: Boolean,
      default: false
    },
    /**
     * Label of the trailing button
     *
     * Required when showTrailingButton is set
     */
    trailingButtonLabel: {
      type: String,
      default: ""
    },
    /**
     * Toggles the success state of the component. Adds a checkmark icon.
     * this cannot be used together with canClear.
     */
    success: {
      type: Boolean,
      default: false
    },
    /**
     * Toggles the error state of the component. Adds an error icon.
     * this cannot be used together with canClear.
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Additional helper text message
     *
     * This will be displayed beneath the input field. In case the field is
     * also marked as having an error, the text will be displayed in red.
     */
    helperText: {
      type: String,
      default: ""
    },
    /**
     * Disable the input field
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Specifies whether the input should have a pill form.
     * By default, input has rounded corners.
     */
    pill: {
      type: Boolean,
      default: false
    },
    /**
     * Class to add to the root component.
     */
    class: {
      type: [Object, String, Array],
      default: ""
    },
    /**
     * Class to add to the input field.
     * Necessary to use NcInputField in the NcActionInput component.
     */
    inputClass: {
      type: [Object, String],
      default: ""
    }
  },
  emits: [
    "update:modelValue",
    "trailing-button-click"
  ],
  computed: {
    computedId() {
      return this.$attrs.id && this.$attrs.id !== "" ? this.$attrs.id : this.inputName;
    },
    inputName() {
      return "input" + GenRandomId();
    },
    hasTrailingIcon() {
      return this.success;
    },
    hasPlaceholder() {
      return this.placeholder !== "" && this.placeholder !== void 0;
    },
    computedPlaceholder() {
      return this.hasPlaceholder ? this.placeholder : this.label;
    },
    isValidLabel() {
      const isValidLabel = this.label || this.labelOutside;
      if (!isValidLabel) {
        console.warn("You need to add a label to the NcInputField component. Either use the prop label or use an external one, as per the example in the documentation.");
      }
      return isValidLabel;
    },
    ariaDescribedby() {
      const ariaDescribedby = [];
      if (this.helperText.length > 0) {
        ariaDescribedby.push(`${this.inputName}-helper-text`);
      }
      if (this.$attrs["aria-describedby"]) {
        ariaDescribedby.push(this.$attrs["aria-describedby"]);
      }
      return ariaDescribedby.join(" ") || null;
    }
  },
  methods: {
    /**
     * Focus the input element
     *
     * @public
     */
    focus() {
      this.$refs.input.focus();
    },
    /**
     * Select all the text in the input
     *
     * @public
     */
    select() {
      this.$refs.input.select();
    },
    handleInput(event) {
      this.$emit("update:modelValue", this.type === "number" && typeof this.modelValue === "number" ? parseFloat(event.target.value, 10) : event.target.value);
    },
    handleTrailingButtonClick(event) {
      this.$emit("trailing-button-click", event);
    }
  }
};
const _hoisted_1 = { class: "input-field__main-wrapper" };
const _hoisted_2 = ["id", "type", "disabled", "placeholder", "aria-describedby", "value"];
const _hoisted_3 = ["for"];
const _hoisted_4 = { class: "input-field__icon input-field__icon--leading" };
const _hoisted_5 = {
  key: 2,
  class: "input-field__icon input-field__icon--trailing"
};
const _hoisted_6 = ["id"];
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_NcButton = resolveComponent("NcButton");
  const _component_Check = resolveComponent("Check");
  const _component_AlertCircle = resolveComponent("AlertCircle");
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["input-field", [{ "input-field--disabled": $props.disabled }, _ctx.$props.class]])
  }, [
    createElementVNode("div", _hoisted_1, [
      createElementVNode("input", mergeProps(_ctx.$attrs, {
        id: $options.computedId,
        ref: "input",
        class: ["input-field__input", [
          $props.inputClass,
          {
            "input-field__input--trailing-icon": $props.showTrailingButton || $options.hasTrailingIcon,
            "input-field__input--leading-icon": !!_ctx.$slots.icon,
            "input-field__input--label-outside": $props.labelOutside,
            "input-field__input--success": $props.success,
            "input-field__input--error": $props.error,
            "input-field__input--pill": $props.pill
          }
        ]],
        type: $props.type,
        disabled: $props.disabled,
        placeholder: $options.computedPlaceholder,
        "aria-describedby": $options.ariaDescribedby,
        "aria-live": "polite",
        value: $props.modelValue.toString(),
        onInput: _cache[0] || (_cache[0] = (...args) => $options.handleInput && $options.handleInput(...args))
      }), null, 16, _hoisted_2),
      !$props.labelOutside && $options.isValidLabel ? (openBlock(), createElementBlock("label", {
        key: 0,
        class: normalizeClass(["input-field__label", [{
          "input-field__label--trailing-icon": $props.showTrailingButton || $options.hasTrailingIcon,
          "input-field__label--leading-icon": !!_ctx.$slots.icon
        }]]),
        for: $options.computedId
      }, toDisplayString($props.label), 11, _hoisted_3)) : createCommentVNode("", true),
      withDirectives(createElementVNode("div", _hoisted_4, [
        renderSlot(_ctx.$slots, "icon", {}, void 0, true)
      ], 512), [
        [vShow, !!_ctx.$slots.icon]
      ]),
      $props.showTrailingButton ? (openBlock(), createBlock(_component_NcButton, {
        key: 1,
        type: "tertiary-no-background",
        class: normalizeClass(["input-field__trailing-button", [{
          "input-field__trailing-button--pill": $props.pill
        }]]),
        "aria-label": $props.trailingButtonLabel,
        disabled: $props.disabled,
        onClick: $options.handleTrailingButtonClick
      }, {
        icon: withCtx(() => [
          renderSlot(_ctx.$slots, "trailing-button-icon", {}, void 0, true)
        ]),
        _: 3
      }, 8, ["class", "aria-label", "disabled", "onClick"])) : $props.success || $props.error ? (openBlock(), createElementBlock("div", _hoisted_5, [
        $props.success ? (openBlock(), createBlock(_component_Check, {
          key: 0,
          size: 20,
          style: { "color": "var(--color-success-text)" }
        })) : $props.error ? (openBlock(), createBlock(_component_AlertCircle, {
          key: 1,
          size: 20,
          style: { "color": "var(--color-error-text)" }
        })) : createCommentVNode("", true)
      ])) : createCommentVNode("", true)
    ]),
    $props.helperText.length > 0 ? (openBlock(), createElementBlock("p", {
      key: 0,
      id: `${$options.inputName}-helper-text`,
      class: normalizeClass(["input-field__helper-text-message", {
        "input-field__helper-text-message--error": $props.error,
        "input-field__helper-text-message--success": $props.success
      }])
    }, [
      $props.success ? (openBlock(), createBlock(_component_Check, {
        key: 0,
        class: "input-field__helper-text-message__icon",
        size: 18
      })) : $props.error ? (openBlock(), createBlock(_component_AlertCircle, {
        key: 1,
        class: "input-field__helper-text-message__icon",
        size: 18
      })) : createCommentVNode("", true),
      createTextVNode(" " + toDisplayString($props.helperText), 1)
    ], 10, _hoisted_6)) : createCommentVNode("", true)
  ], 2);
}
const NcInputField = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__scopeId", "data-v-170179c7"]]);
export {
  NcInputField as N
};
